// The shot/pass event filter bar — ported to React from window.EventMapKit's DOM UI
// (renderers-shotmap.js: renderEventFilterBar / renderEventAddMenu / wireEventFilterBar /
// eventFilterBarHtml). Shared by Heat Map, Shot Map, Pass Map and Passing Profile.
//
// Same consumer contract as <FilterChipBar>: `active` is the module's own mutable
// `_eventFilters` / `_heatmapFilters` object, mutated IN PLACE (add / edit / remove a
// chip, drag a range); `onChange` is the consumer's re-render. Unlike FilterChipBar this
// component renders the whole `.filter-bar.event-filter-bar` row, because the legacy
// builds it as one unit: [Colour-by chip | extra (the table's inline Reset view), …chips,
// add-filter wrap].
//
// Options are cross-filtered (a chip only offers values that co-occur with the OTHER
// active filters) — the pure engine in engines/event-map-kit.ts decides; this is UI.
//
// The pickers take part in the page-wide "one open picker" slot (picker-slot.ts), as the
// legacy's singletons: opening one closes the event picker open anywhere else. Generic over
// the event shape (Zone Finder passes StatsBomb open-data events).

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ValuePicker, RangePicker, popoverPosUnder, type PopoverPos } from "./FilterPopovers.js";
import { closeAllPickers, useSlotPicker } from "./picker-slot.js";
import {
  eventFilterOptions,
  excludeValue,
  categoricalValueOf,
  isExcludeValue,
  formatCategoricalValue,
  formatEventRange,
  formatEventRangeValue,
  rangeValueToPct,
  rangePxToValue,
  newFilterValue,
  type EventActive,
  type EventFilterDef,
  type CategoricalActive,
  type RangeActive,
} from "../engines/event-map-kit.js";
import type { ProtoEvent } from "@catbento/shared";

interface Props<E> {
  active: EventActive;
  /** The unfiltered events on screen (this comp + season [+ action]) — the options source. */
  events: E[];
  defs: EventFilterDef<E>[];
  onChange: () => void;
  /** Extra class on the row (Zone Finder's `zone-event-filter-bar`). */
  className?: string;
  /** Rendered first in the row, before the chips (the table's inline Reset view). */
  extra?: ReactNode;
  /** The Colour-by chip (Shot/Pass Map's pitch view), first in the row. It lives here, not
   *  in `extra`, because the legacy opens its picker through the same one-at-a-time value
   *  picker as the filter chips (key 'colorBy') — so opening one closes the other. */
  colorBy?: ColorByChip;
}

export interface ColorByChip {
  /** The mode labels, in order. */
  options: string[];
  current: string;
  onPick: (label: string) => void;
}

const COLOR_BY_KEY = "colorBy";

type OpenPicker = { key: string; pos: PopoverPos };

export function EventFilterBar<E = ProtoEvent>({ active, events, defs, onChange, extra, colorBy, className }: Props<E>) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Two kinds, as the legacy: a value picker opening leaves an open range picker alone.
  const [valuePicker, openValue, closeValue] = useSlotPicker<OpenPicker>("event", "value");
  const [rangePicker, openRange, closeRange] = useSlotPicker<OpenPicker>("event", "range");
  const addWrapRef = useRef<HTMLDivElement>(null);

  // Outside-click closes the add-menu (the legacy relied on renderers-stats.js's delegated
  // .add-filter-wrap listener; a React-owned menu needs its own).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!addWrapRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  const openPickerAt = (key: string, chipEl: HTMLElement) => {
    const isRange = defs.find((d) => d.key === key)?.type === "range";
    const [current, open, close] = isRange ? [rangePicker, openRange, closeRange] : [valuePicker, openValue, closeValue];
    // Clicking the open chip again toggles it closed (the legacy _anchor check).
    if (current?.key === key) {
      close();
      return;
    }
    open({ key, pos: popoverPosUnder(chipEl) });
  };

  const addFilter = (def: EventFilterDef<E>) => {
    const value = newFilterValue(def, events, active, defs);
    // A categorical with no co-occurring values isn't added — the menu still closes.
    if (value !== undefined) active[def.key] = value;
    setMenuOpen(false);
    onChange();
  };

  const valueDef = valuePicker ? defs.find((d) => d.key === valuePicker.key) : undefined;
  const rangeDef = rangePicker ? defs.find((d) => d.key === rangePicker.key) : undefined;

  return (
    <div className={`filter-bar event-filter-bar${className ? ` ${className}` : ""}`}>
      {colorBy && (
        // A plain pill with no ✕ — a colour mode is always on, there's nothing to clear.
        <div className="filter-chip event-color-by-chip">
          <span
            className="chip-value"
            onClick={(e) => {
              e.stopPropagation();
              openPickerAt(COLOR_BY_KEY, e.currentTarget);
            }}
          >
            Colour: {colorBy.current}
          </span>
        </div>
      )}
      {extra}
      {Object.keys(active).map((key) => {
        const def = defs.find((f) => f.key === key);
        if (!def) return null;
        const label =
          def.type === "range"
            ? `${def.label}: ${formatEventRange(def, active[key] as RangeActive)}`
            : `${def.label}: ${formatCategoricalValue(active[key] as CategoricalActive)}`;
        return (
          <div className="filter-chip event-filter-chip" key={key}>
            <span
              className="chip-value"
              onClick={(e) => {
                e.stopPropagation();
                openPickerAt(key, e.currentTarget);
              }}
            >
              {label}
            </span>
            <span
              className="remove"
              onClick={() => {
                delete active[key];
                // The legacy ✕ closes both event pickers, wherever they're open.
                closeAllPickers("event");
                onChange();
              }}
            >
              ✕
            </span>
          </div>
        );
      })}
      <div className="add-filter-wrap" ref={addWrapRef}>
        <button
          className="add-filter-btn"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((o) => !o);
          }}
        >
          + Add filter
        </button>
        <div className={`add-filter-menu${menuOpen ? " open" : ""}`}>
          {defs.map((def) => {
            const disabled = active[def.key] !== undefined;
            return (
              <div
                key={def.key}
                className={`add-filter-option${disabled ? " disabled" : ""}`}
                data-key={def.key}
                onClick={disabled ? undefined : () => addFilter(def)}
              >
                {def.label}
                {disabled ? " ✓" : ""}
              </div>
            );
          })}
        </div>
      </div>

      {valuePicker?.key === COLOR_BY_KEY && colorBy && (
        <ValuePicker
          label="Colour"
          pos={valuePicker.pos}
          options={colorBy.options}
          activeValue={colorBy.current}
          activeExcluded={false}
          excludable={false}
          onPick={(label) => {
            closeValue();
            colorBy.onPick(label);
          }}
          onClose={closeValue}
        />
      )}
      {valuePicker && valueDef && valueDef.type !== "range" && active[valuePicker.key] !== undefined && (
        <ValuePicker
          label={valueDef.label}
          pos={valuePicker.pos}
          options={eventFilterOptions(events, valueDef, active, defs)}
          activeValue={categoricalValueOf(active[valuePicker.key] as CategoricalActive)}
          activeExcluded={isExcludeValue(active[valuePicker.key])}
          onPick={(value, exclude) => {
            active[valuePicker.key] = exclude ? excludeValue(value) : value;
            closeValue();
            onChange();
          }}
          onClose={closeValue}
        />
      )}
      {rangePicker && rangeDef && rangeDef.type === "range" && active[rangePicker.key] !== undefined && (
        <RangePicker
          pos={rangePicker.pos}
          range={active[rangePicker.key] as RangeActive}
          valueToPct={(v) => rangeValueToPct(rangeDef, v)}
          pxToValue={(px, w) => rangePxToValue(rangeDef, px, w)}
          formatReadout={(range) => formatEventRange(rangeDef, range)}
          labels={
            <>
              <span>{formatEventRangeValue(rangeDef, rangeDef.domain.min)}</span>
              <span>{formatEventRangeValue(rangeDef, rangeDef.domain.max)}</span>
            </>
          }
          onChange={onChange}
          onClose={closeRange}
        />
      )}
    </div>
  );
}
